import { RefObject } from "react"

import { isClientSide } from "@/lib/is-client-side"

export function isTouch() {
  if (isClientSide()) {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0
  }

  return false
}

export function isIOS() {
  if (isClientSide()) {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
  }

  return false
}

export function isSafari() {
  if (isClientSide()) {
    return /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(
      navigator.userAgent
    )
  }

  return false
}

export function lockBodyScroll() {
  if (isIOS()) {
    Object.assign(document.body.style, {
      position: "fixed",
      top: window.scrollY * -1 + "px",
      width: "100%",
      overflowY: "",
    })
    document.documentElement.style.height =
      document.documentElement.scrollHeight + "px"
    document.documentElement.style.overflow = "hidden"
  } else {
    document.body.style.overflow = "hidden"
  }
}

export function scrollPageToTop() {
  if (!isClientSide()) return
  window.scrollTo({ top: 0, left: 0 })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

/** scroll after paint and route scroll restoration (shallow routing) */
export function scrollPageToTopAfterPaint() {
  scrollPageToTop()
  requestAnimationFrame(() => {
    scrollPageToTop()
    requestAnimationFrame(scrollPageToTop)
  })
  setTimeout(scrollPageToTop, 0)
}

export function unlockBodyScroll() {
  if (isIOS()) {
    document.documentElement.style.height = ""
    document.documentElement.style.overflow = ""
    const scrollPosition = parseInt(document.body.style.top, 10) * -1
    Object.assign(document.body.style, {
      position: "",
      top: "",
      width: "",
    })
    window.scrollTo(0, scrollPosition)
  } else {
    document.body.style.overflow = ""
  }
}

export function isElementInViewportX(
  el: Element,
  container?: RefObject<HTMLElement | null>
): boolean {
  const rect = el.getBoundingClientRect()
  const containerWidth = container?.current?.clientWidth || window.innerWidth
  return rect.left >= 0 && rect.right <= containerWidth
}

export const scrollToActiveItemX = (
  scrollRef: RefObject<HTMLElement | null>,
  activeSelector: string,
  container?: RefObject<HTMLElement | null>
) => {
  if (scrollRef.current) {
    const activeItem = scrollRef.current.querySelector(activeSelector)
    if (
      activeItem instanceof HTMLElement &&
      !isElementInViewportX(activeItem, container)
    ) {
      const itemCenterX = activeItem.offsetLeft + activeItem.offsetWidth / 2
      const containerCenterX = scrollRef.current.clientWidth / 2
      scrollRef.current.scrollLeft = itemCenterX - containerCenterX
    }
  }
}

export type Section = {
  ref: RefObject<HTMLDivElement | null>
  id: string
}

export type Sections = Record<string, Section>

// react rerendering can occasionally mess up the scroll position in safari.
// seems to happen if the component that is rerendering is at the bottom of the scrolling ancestor,
// and the user has scrolled all the way down. for example, the latest chat message.
export function fixScrollPosAfterRender(scrollContainer: HTMLElement | null) {
  // allow null for easier usage with refs
  if (scrollContainer) {
    const { scrollTop, scrollLeft } = scrollContainer
    setTimeout(() => {
      scrollContainer.scrollTop = scrollTop
      scrollContainer.scrollLeft = scrollLeft
    }, 1)
  }
}

export const preventDefault = (e: Event) => e.preventDefault()

const INTERACTIVE_DESCENDANT_SELECTOR = "a, button, [role='button']"

/**
 * Stop an event that began on a link, button, or role=button nested inside the
 * element handling it. Events from the rest of that element keep bubbling, so a
 * parent control can still handle clicks on its empty space.
 */
export function stopIfInteractiveDescendant(event: {
  target: EventTarget | null
  currentTarget: EventTarget | null
  stopPropagation(): void
}) {
  const { target, currentTarget } = event
  if (!(target instanceof Element) || !(currentTarget instanceof Element)) {
    return
  }
  const interactive = target.closest(INTERACTIVE_DESCENDANT_SELECTOR)
  if (
    interactive &&
    interactive !== currentTarget &&
    currentTarget.contains(interactive)
  ) {
    event.stopPropagation()
  }
}

// Upper bound on how long the throwaway keyboard input stays mounted while
// waiting for the hand-off to the real input. Needs to comfortably exceed a
// drawer's open transition (plus the frames Dialog defers it by).
const SUMMON_KEYBOARD_TIMEOUT_MS = 2000

// Same, for a dialog whose chunk has yet to load: on first open the chunk is
// compiled (dev) or fetched over a slow link before the drawer even starts
// its transition, so the in-page budget is not enough. A dialog that never
// mounts leaves an invisible focused input behind for at most this long.
const PENDING_DIALOG_KEYBOARD_TIMEOUT_MS = 10_000

export type KeyboardHandOff = (
  input: HTMLInputElement | HTMLTextAreaElement | null,
  options?: { select?: boolean }
) => void

// iOS Safari only opens the virtual keyboard when focus() runs inside the
// synchronous user-gesture call stack. When an input is conditionally mounted
// (e.g. revealed by the same tap), focusing it after the re-render — via rAF or
// setTimeout — lands outside the gesture and iOS suppresses the keyboard. To
// work around this we focus a throwaway input during the tap to summon the
// keyboard, then transfer focus to the real input once it renders. Moving focus
// between inputs while the keyboard is already up doesn't dismiss it.
//
// Call this synchronously inside the tap handler. The returned hand-off moves
// focus to the real input and removes the throwaway one; call it once the real
// input is mounted AND in its final on-screen position. Handing off while the
// input is still animating in from off-screen makes iOS scroll the window to
// reveal it, dragging fixed-position containers (drawers) out of view.
//
// Removing the focused throwaway input before the hand-off would blur it and
// dismiss the keyboard, so it stays mounted until the hand-off or the deadline.
export function summonKeyboard(
  timeoutMs = SUMMON_KEYBOARD_TIMEOUT_MS
): KeyboardHandOff {
  if (!isClientSide()) return () => {}

  const tempInput = createKeyboardSummonInput()
  document.body.appendChild(tempInput)
  tempInput.focus()

  const timeoutId = window.setTimeout(() => {
    tempInput.remove()
  }, timeoutMs)

  return (input, options) => {
    window.clearTimeout(timeoutId)
    input?.focus()
    if (options?.select) {
      input?.select()
    }
    tempInput.remove()
  }
}

/**
 * Whether the browser still considers us inside a user gesture. Older browsers
 * lack the API; assume we are rather than skip gesture-only work.
 */
export function hasTransientUserActivation() {
  if (!isClientSide()) return false
  return navigator.userActivation?.isActive ?? true
}

// A keyboard summoned for a Dialog that is not mounted yet — one whose chunk
// loads with next/dynamic once a Redux flag flips (see LazyDialog). By the time
// that Dialog mounts, the tap is long over, so it cannot summon the keyboard
// itself; instead the flip arms this slot inside the tap and the Dialog claims
// it on open. The stand-in expires on its own timeout if nothing claims it.
let pendingDialogKeyboard: KeyboardHandOff | null = null

export function summonKeyboardForPendingDialog(): void {
  if (!isClientSide()) return
  // Summon the new stand-in before dropping any previous one: removing a
  // focused input would blur it and dismiss the keyboard.
  const previous = pendingDialogKeyboard
  pendingDialogKeyboard = summonKeyboard(PENDING_DIALOG_KEYBOARD_TIMEOUT_MS)
  previous?.(null)
}

export function claimPendingDialogKeyboard(): KeyboardHandOff | null {
  const pending = pendingDialogKeyboard
  pendingDialogKeyboard = null
  return pending
}

// Convenience wrapper around summonKeyboard for inputs that render in place
// (no enter animation): polls each frame and hands off as soon as the input
// mounts. The input may take several frames to appear (e.g. Dialog defers the
// drawer's `open` by two frames), so we don't assume it exists after one.
export function summonKeyboardForPendingInput(
  getInput: () => HTMLInputElement | null,
  options?: { select?: boolean }
) {
  if (!isClientSide()) return

  const handOff = summonKeyboard()
  const deadline = performance.now() + SUMMON_KEYBOARD_TIMEOUT_MS

  const tryHandOff = () => {
    const input = getInput()
    if (input) {
      handOff(input, options)
      return
    }

    if (performance.now() < deadline) {
      requestAnimationFrame(tryHandOff)
      return
    }

    // Give up — the input never mounted within the deadline.
    handOff(null)
  }

  requestAnimationFrame(tryHandOff)
}

function createKeyboardSummonInput() {
  const tempInput = document.createElement("input")
  tempInput.type = "text"
  tempInput.setAttribute("aria-hidden", "true")
  tempInput.tabIndex = -1
  Object.assign(tempInput.style, {
    position: "fixed",
    top: "0",
    left: "0",
    height: "0",
    width: "0",
    opacity: "0",
    border: "0",
    padding: "0",
    // Avoid the iOS auto-zoom that triggers for inputs under 16px.
    fontSize: "16px",
  })
  return tempInput
}

export const inspirationScrollTo = (
  id: string,
  behavior: ScrollBehavior = "instant"
) => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: behavior })
  }
}
